import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Mail, MessageSquare, MessageCircle, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel, Empty } from "@/components/PageHeader";
import { api, fdatetime } from "@/lib/api";

const ICON = { email: Mail, sms: MessageSquare, whatsapp: MessageCircle };
const LABEL = { email: "Email", sms: "SMS", whatsapp: "WhatsApp" };

export const MessageLog = () => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [channel, setChannel] = useState("all");
  const [status, setStatus] = useState("all");
  const [exporting, setExporting] = useState(false);
  const params = { q, channel: channel === "all" ? "" : channel, status: status === "all" ? "" : status };
  useEffect(() => {
    const t = setTimeout(() => api.get("/customer-messages", { params }).then((r) => setRows(r.data)).catch(() => setRows([])), 300);
    return () => clearTimeout(t);
  }, [q, channel, status]); // eslint-disable-line react-hooks/exhaustive-deps
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api.get("/customer-messages/export", { params, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = `messaggi-singoli-${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      toast.success("Storico esportato in Excel");
    } catch (e) { toast.error("Esportazione non riuscita"); } finally { setExporting(false); }
  };

  return (
    <Panel title="Storico messaggi singoli (scheda cliente)" testId="message-log">
      <div className="flex flex-wrap gap-2 border-b border-slate-100 px-5 py-3">
        <div className="relative min-w-[240px] flex-1"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input data-testid="msglog-search" className="pl-8" placeholder="Cerca cliente, destinatario, testo, operatore" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <Select value={channel} onValueChange={setChannel}><SelectTrigger className="w-36" data-testid="msglog-channel"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Tutti i canali</SelectItem><SelectItem value="email">Email</SelectItem><SelectItem value="sms">SMS</SelectItem><SelectItem value="whatsapp">WhatsApp</SelectItem></SelectContent></Select>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-36" data-testid="msglog-status"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Tutti gli esiti</SelectItem><SelectItem value="inviato">Inviato</SelectItem><SelectItem value="fallito">Fallito</SelectItem></SelectContent></Select>
        <Button variant="outline" data-testid="msglog-export" onClick={exportXlsx} disabled={exporting || !rows?.length}><Download className="mr-2 h-4 w-4" />{exporting ? "Esporto…" : "Excel"}</Button>
      </div>
      <div className="max-h-[520px] overflow-auto">
        {!rows ? <div className="p-5 text-sm text-slate-400">Caricamento...</div> : rows.length === 0 ? <Empty text="Nessun messaggio trovato." /> : rows.map((m) => {
          const Icon = ICON[m.channel];
          return (
            <div key={m.id} className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-3 last:border-0" data-testid={`msglog-row-${m.id}`}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Icon className="h-4 w-4 text-slate-400" />
                  <Link to={`/clienti/${m.customer_id}`} className="font-medium text-slate-800 hover:text-blue-600" data-testid={`msglog-customer-${m.id}`}>{m.customer_name || m.customer_id}</Link>
                  <span className="text-xs text-slate-400">{LABEL[m.channel]} · <span className="font-mono">{m.to}</span></span>
                </div>
                {m.subject && <div className="mt-0.5 text-xs font-medium text-slate-700">{m.subject}</div>}
                <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{m.body}</p>
                {m.error && <p className="mt-0.5 text-[11px] text-rose-600">{m.error}</p>}
                <div className="mt-1 text-[11px] text-slate-400">{fdatetime(m.created_at)} · {m.sent_by}</div>
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${m.status === "inviato" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{m.status}</span>
            </div>
          );
        })}
      </div>
    </Panel>
  );
};
